import { LitElement, html, css, nothing } from "lit";
import { customElement, property, state } from "lit/decorators.js";
import { api } from "../api";

const SAFE_NAME_PATTERN = /^[a-z0-9][a-z0-9-]*$/;

@customElement("create-plm-product-dialog")
export class CreatePlmProductDialog extends LitElement {
  static styles = css`
    :host {
      display: block;
      font-family: Geist, sans-serif;
    }

    .form-field {
      margin-bottom: var(--spacing-16, 16px);
    }

    .form-field label {
      display: block;
      font-size: 13px;
      font-weight: 600;
      color: var(--colors-foreground-fg-gray-700, #5a6072);
      margin-bottom: var(--spacing-4, 4px);
    }

    .form-field .help {
      font-size: 11px;
      color: var(--colors-foreground-fg-gray-500, #98a2b3);
      margin-top: var(--spacing-4, 4px);
    }

    .modal-footer {
      display: flex;
      gap: var(--spacing-8, 8px);
      justify-content: flex-end;
    }

    .success-message {
      text-align: center;
      padding: var(--spacing-16, 16px) 0;
    }

    bee-alert {
      margin-bottom: var(--spacing-16, 16px);
    }
  `;

  @property({ type: Boolean }) open = false;
  @property() tenant = "";
  @property() instance = "";

  @state() private _name = "";
  @state() private _hostname = "";
  @state() private _versions = "";
  @state() private _applying = false;
  @state() private _error = "";

  private get _isValid(): boolean {
    return SAFE_NAME_PATTERN.test(this._name.trim());
  }

  private _close() {
    this._name = "";
    this._hostname = "";
    this._versions = "";
    this._error = "";
    this._applying = false;
    this.dispatchEvent(new CustomEvent("close"));
  }

  private async _submit() {
    if (!this._isValid || this._applying) return;
    this._applying = true;
    this._error = "";
    try {
      const versions = this._versions
        ? this._versions.split(",").map((s) => s.trim()).filter(Boolean)
        : undefined;
      await api.createPlmProduct({
        tenant: this.tenant,
        instance: this.instance,
        name: this._name.trim(),
        hostname: this._hostname.trim() || undefined,
        versions,
      });
      this.dispatchEvent(new CustomEvent("plm-product-created", { detail: { name: this._name.trim() } }));
      this._close();
    } catch (e: unknown) {
      this._error = e instanceof Error ? e.message : "Failed to create PLM product";
    } finally {
      this._applying = false;
    }
  }

  render() {
    if (!this.open) return nothing;

    return html`
      <bee-modal open in-root title="Add PLM Product — ${this.tenant} / ${this.instance}" @bee-request-close=${this._close}>
        <div class="form-field">
          <label>Product Name</label>
          <bee-input
            placeholder="e.g., teamcenter"
            .value=${this._name}
            @bee-input=${(e: Event) => { this._name = (e.target as HTMLInputElement).value.trim().toLowerCase(); }}
          ></bee-input>
          <div class="help">Lowercase alphanumeric with hyphens.</div>
        </div>

        <div class="form-field">
          <label>Hostname (optional)</label>
          <bee-input
            placeholder="e.g., platform.acme.com"
            .value=${this._hostname}
            @bee-input=${(e: Event) => { this._hostname = (e.target as HTMLInputElement).value.trim(); }}
          ></bee-input>
        </div>

        <div class="form-field">
          <label>Versions (optional)</label>
          <bee-input
            placeholder="e.g., 14.3.0.1, 14.3.0.2"
            .value=${this._versions}
            @bee-input=${(e: Event) => { this._versions = (e.target as HTMLInputElement).value; }}
          ></bee-input>
          <div class="help">Comma-separated version strings.</div>
        </div>

        ${this._error ? html`<bee-alert variant="error" open><span slot="message">${this._error}</span></bee-alert>` : nothing}

        <div slot="footer" class="modal-footer">
          <bee-button label="Cancel" bee-type="secondary-grey" @click=${this._close} ?disabled=${this._applying}></bee-button>
          <bee-button
            label=${this._applying ? "Creating..." : "Add PLM Product"}
            bee-type="primary"
            @click=${this._submit}
            ?disabled=${!this._isValid || this._applying}
            ?loading=${this._applying}
          ></bee-button>
        </div>
      </bee-modal>
    `;
  }
}
